import { useSyncExternalStore } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { DeviceInfo, DeviceInfoKey } from 'src/constants/LocalStorageKeys';

/**
 * An observable view of the stored device identity.
 *
 * Device identity arrives after mount in the native shell (a Capacitor bridge round-trip), and
 * `react-use`'s `useLocalStorage` holds a per-instance snapshot that would never show it.
 * Consumers subscribe here instead, and `writeDeviceInfo` is the single writer.
 */

const listeners = new Set<() => void>();

// useSyncExternalStore re-renders whenever the snapshot is referentially new, so the parsed object
// is cached against the raw string it came from and only rebuilt when that actually changes.
let cache: { raw: string | null; parsed: DeviceInfo | undefined } = {
    raw: null,
    parsed: undefined,
};

function parse(raw: string | null): DeviceInfo | undefined {
    if (!raw) {
        return undefined;
    }
    try {
        return JSON.parse(raw) as DeviceInfo;
    } catch (error) {
        datadogRum.addError(error, { message: 'Unable to parse stored device info' });
        return undefined;
    }
}

// getSnapshot runs on every render, so a blocked store would report on every one of them.
let reportedReadFailure = false;

function getSnapshot(): DeviceInfo | undefined {
    let raw: string | null;
    try {
        raw = localStorage.getItem(DeviceInfoKey);
    } catch (error) {
        // Reads throw in the same conditions writes do, e.g. storage blocked outright.
        if (!reportedReadFailure) {
            reportedReadFailure = true;
            datadogRum.addError(error, { message: 'Unable to read device info from localStorage' });
        }
        return undefined;
    }

    if (raw !== cache.raw) {
        cache = { raw, parsed: parse(raw) };
    }
    return cache.parsed;
}

function subscribe(onStoreChange: () => void): () => void {
    listeners.add(onStoreChange);
    // Covers writes from another document; same-document writes come through writeDeviceInfo.
    window.addEventListener('storage', onStoreChange);
    return () => {
        listeners.delete(onStoreChange);
        window.removeEventListener('storage', onStoreChange);
    };
}

/**
 * Persists the device identity and notifies every observer, so none of them needs a reload.
 *
 * A storage failure is reported, never thrown: the callers sit in a useEffect and a promise
 * callback, where throwing would take out the app or silently skip hydration.
 */
export function writeDeviceInfo(next: DeviceInfo): void {
    try {
        localStorage.setItem(DeviceInfoKey, JSON.stringify(next));
    } catch (error) {
        datadogRum.addError(error, { message: 'Unable to persist device info to localStorage' });
    }

    listeners.forEach((listener) => listener());
}

export function useDeviceInfo(): DeviceInfo | undefined {
    return useSyncExternalStore(subscribe, getSnapshot);
}
